import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
	FormField,
	FormLabel,
	RuPrimaryButton,
	RuSecondaryButton,
	SelectField,
	Warning,
} from '@silverrailtech/srtlibrary';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import messages from '../../../messagesCustomerAccounts';
import CreateCustomerAccountDialog from './CreateCustomerAccountDialog';

import {
	PageHeader,
	SearchFormWrapper,
	SearchTooltipWrapper,
	SearchButtonWrapper,
	SplitRow,
	SplitCol,
	Tooltip,
	EmailWarning,
	SearchModeTabsWrapper,
	StyledTabs,
	StyledTab,
} from './AccountSearchStyled';

import {
	getCustomerAccountData,
	postCustomerAccountData,
} from '../../../../api';
import { setCustomerAccountData } from '../../../actionsCustomerAccount';
import useRequestor from '../../../../hooks/useRequestor';
import useAuth from '../../../../hooks/useAuth';
import { setCustomersActiveRealm } from '../../../../actions/authActions';
import { LOCAL_STORAGE_KEYS } from '../../../../constants/constants';
import ExtendedSnackbar from '../../../../components/ExtendedSnackbar/ExtendedSnackbar';

const SEARCH_MODES = {
	EMAIL: 'email',
	ACCOUNT_ID: 'accountId',
};
// import {
// 	sanitizeFormData,
// 	SANITIZERS,
// 	sanitizeValue,
// } from '../../../../utils/sanitize';

const AccountSearch = ({
	intl: { formatMessage },
	setCustomersActiveRealm: setCustomersActiveRealmAction,
}) => {
	const dispatch = useDispatch();

	const { getAuthHeaders } = useAuth();
	const [formData, setFormData] = useState({
		railOperator: '',
		email: '',
		accountId: '',
	});

	const { accountRealms, accountRealmsOptions, apiCallWithAuthRetry } =
		useRequestor();

	const [searchMode, setSearchMode] = useState(SEARCH_MODES.EMAIL);
	const [isEmailValid, setIsEmailValid] = useState(false);
	const [formError, setFormError] = useState(false);
	const [accountNotFound, setAccountNotFound] = useState(false);
	const [accountIdNotFound, setAccountIdNotFound] = useState(false);
	const [isFormSubmitted, setIsFormSubmitted] = useState(false);

	const isAccountIdMode = searchMode === SEARCH_MODES.ACCOUNT_ID;
	const isAccountIdValid = formData.accountId.trim().length > 0;

	const selectedRealmLabel =
		accountRealms.find(
			(r) => r.SecurityAuthenticationRealmName === formData.railOperator,
		)?.SecurityAuthenticationAccountGroupLabel || '';

	// Create account dialog state
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [isCreatingAccount, setIsCreatingAccount] = useState(false);
	const [createAccountError, setCreateAccountError] = useState(null);
	const [createAccountSuccess, setCreateAccountSuccess] = useState(false);

	const handleChange = (name, value) => {
		setFormData((prev) => ({ ...prev, [name]: value }));

		if (name !== 'railOperator') return;
		const selectedRealm =
			accountRealms.find(
				(realm) => realm.SecurityAuthenticationRealmName === value,
			) || null;
		if (selectedRealm?.SecurityAuthenticationRealmToken) {
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.customerAccountToken,
				selectedRealm.SecurityAuthenticationRealmToken,
			);
		}
		const { SecurityAuthenticationRealmToken, ...realmWithoutToken } =
			selectedRealm || {};
		setCustomersActiveRealmAction(selectedRealm ? realmWithoutToken : null);
	};

	const handleSearchModeChange = (mode) => {
		if (mode === searchMode) {
			return;
		}

		setSearchMode(mode);
		setFormError(false);
		setAccountNotFound(false);
		setAccountIdNotFound(false);
	};

	// const handleEmailBlur = () => {
	// 	const sanitizedFormData = sanitizeFormData(formData, {
	// 		email: SANITIZERS.email,
	// 	});

	// 	if (sanitizedFormData.email !== formData.email) {
	// 		setFormData((prev) => ({
	// 			...prev,
	// 			email: sanitizedFormData.email,
	// 		}));
	// 	}
	// };

	useEffect(() => {
		if (accountRealms && accountRealms.length > 0 && !formData.railOperator) {
			const defaultRealm = accountRealms[0];
			setFormData((prev) => ({
				...prev,
				railOperator: defaultRealm.SecurityAuthenticationRealmName,
			}));
			if (defaultRealm?.SecurityAuthenticationRealmToken) {
				localStorage.setItem(
					LOCAL_STORAGE_KEYS.customerAccountToken,
					defaultRealm.SecurityAuthenticationRealmToken,
				);
			}
			const { SecurityAuthenticationRealmToken, ...realmWithoutToken } =
				defaultRealm;
			setCustomersActiveRealmAction(realmWithoutToken);
		}
	}, [accountRealms, formData.railOperator, setCustomersActiveRealmAction]);

	const searchAccount = () => {
		// const sanitizedFormData = sanitizeFormData(formData, {
		// 	email: SANITIZERS.email,
		// });

		const { parameters, customPath, onApiError, onNotFound } = isAccountIdMode
			? {
					parameters: null,
					customPath: `/${formData.accountId.trim()}`,
					onApiError: () => setAccountIdNotFound(true),
					onNotFound: () => setAccountIdNotFound(true),
				}
			: {
					// TODO: UPDATE TO USING sanitizedFormData
					parameters: { email: formData.email },
					customPath: '',
					onApiError: () => setFormError(true),
					onNotFound: () => setAccountNotFound(true),
				};

		apiCallWithAuthRetry({
			apiCall: (onError, onSuccess, refreshedHeaders) => {
				getCustomerAccountData(
					parameters,
					refreshedHeaders || getAuthHeaders(),
					customPath,
					onError,
					onSuccess,
				);
			},
			realmName: formData.railOperator,
			onError: () => {
				setIsFormSubmitted(false);
				onApiError();
			},
			onSuccess: (data) => {
				setIsFormSubmitted(false);
				if (!data?.accountId) {
					onNotFound();
				} else {
					dispatch(setCustomerAccountData(data.accountId));
				}
			},
		});
	};

	const handleSubmit = (event) => {
		event.preventDefault();

		// const sanitizedFormData = sanitizeFormData(formData, {
		// 	email: SANITIZERS.email,
		// });

		// if (sanitizedFormData.email !== formData.email) {
		// 	setFormData((prev) => ({
		// 		...prev,
		// 		email: sanitizedFormData.email,
		// 	}));
		// }

		setIsFormSubmitted(true);
		setFormError(false);
		setAccountNotFound(false);
		setAccountIdNotFound(false);
		searchAccount();
	};

	const handleOpenCreateDialog = () => {
		setFormError(false);
		setAccountNotFound(false);
		setCreateAccountError(null);
		setIsCreateDialogOpen(true);
	};

	const handleCloseCreateDialog = () => {
		setIsCreateDialogOpen(false);
		setCreateAccountError(null);
	};

	const createAccount = (createFormData) => {
		// const sanitizedCreateFormData = sanitizeFormData(createFormData, {
		// 	email: SANITIZERS.email,
		// });

		const accountData = {
			title: 'Mr',
			// TODO: UPDATE TO USING sanitizedCreateFormData
			firstName: createFormData.firstName,
			// TODO: UPDATE TO USING sanitizedCreateFormData
			lastName: createFormData.lastName,
			// TODO: UPDATE TO USING sanitizedCreateFormData
			email: createFormData.email,
		};

		apiCallWithAuthRetry({
			apiCall: (onError, onSuccess, refreshedHeaders) => {
				postCustomerAccountData(
					accountData,
					refreshedHeaders || getAuthHeaders(),
					'',
					onError,
					onSuccess,
				);
			},
			realmName: formData.railOperator,
			onError: (error) => {
				const errorCode = error?.errorResponse?.code || error?.status;
				const errorMessage = error?.errorResponse?.message || error?.message;
				if (
					errorCode === 409 ||
					errorMessage?.toLowerCase().includes('exists')
				) {
					setIsCreatingAccount(false);
					setCreateAccountError(
						formatMessage(messages.lblCreateAccountEmailExists),
					);
					return;
				}
				setIsCreatingAccount(false);
				setCreateAccountError(formatMessage(messages.lblCreateAccountError));
			},
			onSuccess: (data) => {
				setIsCreatingAccount(false);
				setIsCreateDialogOpen(false);
				setAccountNotFound(false);
				setCreateAccountSuccess(true);

				if (data?.accountId) {
					dispatch(setCustomerAccountData(data.accountId));
				}
			},
		});
	};

	const handleCreateAccount = (createFormData) => {
		setIsCreatingAccount(true);
		setCreateAccountError(null);
		createAccount(createFormData);
	};

	return (
		<div className="container">
			<div className="row">
				<div className="col-12">
					<PageHeader>
						<h1>
							<FormattedMessage {...messages.lblSearchTitle} />
						</h1>
						<RuSecondaryButton
							color="positive"
							endIcon={<PersonOutlineOutlinedIcon />}
							onClick={handleOpenCreateDialog}
							data-testid="CreateCustomerAccountButton"
						>
							<FormattedMessage {...messages.lblCreateAccountButton} />
						</RuSecondaryButton>
					</PageHeader>
					<form onSubmit={handleSubmit}>
						<SearchFormWrapper>
							<SearchModeTabsWrapper>
								<StyledTabs
									value={searchMode}
									onChange={(event, value) => handleSearchModeChange(value)}
								>
									<StyledTab
										id="searchByEmailTab"
										data-testid="SearchByEmailTabButton"
										value={SEARCH_MODES.EMAIL}
										label={formatMessage(messages.lblSearchTabEmail)}
									/>
									<StyledTab
										id="searchByAccountIdTab"
										data-testid="SearchByAccountIdTabButton"
										value={SEARCH_MODES.ACCOUNT_ID}
										label={formatMessage(messages.lblSearchTabAccountId)}
									/>
								</StyledTabs>
							</SearchModeTabsWrapper>

							{accountRealmsOptions.length > 0 && (
								<SplitRow>
									<SplitCol>
										<FormLabel
											id="railOperator"
											label={formatMessage(messages.lblSearchOperatorLabel)}
										/>
										<SelectField
											name="toc"
											id="toc"
											value={formData.railOperator}
											onChange={(e) =>
												handleChange('railOperator', e.target.value)
											}
											options={accountRealmsOptions}
											displayEmpty
											placeholder={formatMessage(
												messages.lblSearchOperatorEmptyPlaceholder,
											)}
										/>
									</SplitCol>
									<SplitCol hideOnSmall>
										<SearchTooltipWrapper>
											<Tooltip>
												<FormattedMessage
													{...messages.lblSearchOperatorTooltip}
												/>
											</Tooltip>
										</SearchTooltipWrapper>
									</SplitCol>
								</SplitRow>
							)}

							{formError && (
								<SplitRow
									padding={accountRealmsOptions.length > 0 ? '16px 0' : '0'}
								>
									<SplitCol>
										<EmailWarning
											text={formatMessage(messages.lblSearchEmailError)}
											variant="important"
										/>
									</SplitCol>
								</SplitRow>
							)}

							{accountNotFound && (
								<SplitRow
									padding={accountRealmsOptions.length > 0 ? '16px 0' : '0'}
								>
									<SplitCol>
										<Warning
											text={formatMessage(messages.lblSearchAccountNotFound)}
											variant="noteworthy"
										/>
									</SplitCol>
								</SplitRow>
							)}

							{accountIdNotFound && (
								<SplitRow
									padding={accountRealmsOptions.length > 0 ? '16px 0' : '0'}
								>
									<SplitCol>
										<EmailWarning
											text={formatMessage(messages.lblSearchAccountIdError)}
											variant="important"
										/>
									</SplitCol>
								</SplitRow>
							)}
							<SplitRow>
								<SplitCol>
									{isAccountIdMode ? (
										<FormField
											fieldName="accountId"
											label={formatMessage(messages.lblSearchAccountIdLabel)}
											fieldValue={formData.accountId}
											onChange={(value) =>
												handleChange('accountId', value.trim())
											}
										/>
									) : (
										<FormField
											fieldName="email"
											label={formatMessage(messages.lblSearchEmailLabel)}
											fieldValue={formData.email}
											onChange={(value) =>
												handleChange(
													'email',
													value.trim(),
													// TODO: sanitizeValue(value, SANITIZERS.email),
												)
											}
											// TODO: onBlur={handleEmailBlur}
											validation="email"
											isFieldValid={isEmailValid}
											setIsValid={setIsEmailValid}
										/>
									)}
								</SplitCol>
								<SplitCol hideOnSmall>
									<SearchTooltipWrapper>
										<Tooltip>
											<FormattedMessage
												{...(isAccountIdMode
													? messages.lblSearchAccountIdTooltip
													: messages.lblSearchEmailTooltip)}
											/>
										</Tooltip>
									</SearchTooltipWrapper>
								</SplitCol>
							</SplitRow>
						</SearchFormWrapper>

						<SearchButtonWrapper>
							<RuPrimaryButton
								type="submit"
								color="neutral"
								data-testid="SearchCustomerSubmitButton"
								aria-label={formatMessage(messages.lblSearchSubmitButton)}
								fullWidth
								isAsync={isFormSubmitted}
								disabled={
									(isAccountIdMode ? !isAccountIdValid : !isEmailValid) ||
									(accountRealmsOptions.length > 0 && !formData.railOperator)
								}
							>
								<FormattedMessage {...messages.lblSearchSubmitButton} />
							</RuPrimaryButton>
						</SearchButtonWrapper>
					</form>

					<CreateCustomerAccountDialog
						isOpen={isCreateDialogOpen}
						onClose={handleCloseCreateDialog}
						onSubmit={handleCreateAccount}
						initialEmail={formData.email}
						isSubmitting={isCreatingAccount}
						error={createAccountError}
						tocName={selectedRealmLabel}
					/>
				</div>
			</div>
			<ExtendedSnackbar
				id="srtCreateAccountSnackBar"
				open={createAccountSuccess}
				message={formatMessage(messages.lblCreateAccountSuccess)}
				onClose={() => setCreateAccountSuccess(false)}
			/>
		</div>
	);
};

AccountSearch.propTypes = {
	intl: PropTypes.object.isRequired,
	setCustomersActiveRealm: PropTypes.func,
};

const mapStateToProps = (state) => ({
	activeRealm: state.getIn(['auth', 'customersActiveRealm']),
});

const mapDispatchToProps = {
	setCustomersActiveRealm,
};

export default injectIntl(
	connect(mapStateToProps, mapDispatchToProps)(AccountSearch),
);
